/**
 * NetworkWatchdog — E2E Chaos Audit, Bloco D
 * Intercepta requisições fetch globais e exibe Toast quando ocorrem erros HTTP críticos.
 *
 * Em DEV: GET para localhost:5173/ com ERR_CONNECTION_REFUSED é normal quando
 * o servidor Vite está parado ou reiniciando (HMR tenta reconectar).
 */
import { useEffect, useRef } from "react";
import { useToast } from "@/shared/hooks/use-toast";

const CRITICAL_STATUSES = [400, 401, 403, 413, 500, 502, 503, 504];

/** Clona a resposta para ler o body sem consumir o original */
async function cloneAndReadBody(res: Response): Promise<string> {
  try {
    const clone = res.clone();
    const text = await clone.text();
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text.slice(0, 2000) + (text.length > 2000 ? "..." : "");
    }
  } catch {
    return "[Could not read body]";
  }
}

export function NetworkWatchdog() {
  const { toast } = useToast();
  const installed = useRef(false);

  useEffect(() => {
    if (installed.current) return;
    installed.current = true;

    const originalFetch = window.fetch;
    window.fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method ?? "GET";

      const res = await originalFetch.call(window, input, init);

      if (CRITICAL_STATUSES.includes(res.status)) {
        const resBody = await cloneAndReadBody(res);
        let errCode = "";
        let skipToast = false;
        try {
          const parsed = JSON.parse(resBody);
          if (parsed?.code) errCode = ` [${parsed.code}]`;
          else if (parsed?.error) errCode = ` [${parsed.error}]`;
          const msg = String(parsed?.message ?? parsed?.error ?? "");
          // Não exibir toast para erros de schema que o app já trata com fallback
          // (migration atrasada: coluna inexistente, tabela não encontrada)
          skipToast =
            parsed?.code === "42703" ||
            /column.*does not exist/i.test(msg) ||
            /could not find the table/i.test(msg) ||
            /profiles_public|ai_drafts\.status|client_contacts/i.test(msg) ||
            /session.*session_id|invalid refresh token|invalid login/i.test(msg);
        } catch {
          /* ignore */
        }
        // 401 é logado no console mas não gera toast — o app já tem tratamento de sessão expirada
        skipToast = skipToast || res.status === 401;
        if (!skipToast) {
          const msg = `Erro ${res.status}${errCode} - ${method} ${url}`;
          toast({
            title: "Erro de rede",
            description: msg,
            variant: "destructive",
          });
        }
      }

      return res;
    };
  }, [toast]);

  return null;
}
